import type { CustomerSummary } from "@kopi/contracts";
import { CUSTOMER_SORTS, DEFAULT_CUSTOMER_SORT } from "@kopi/contracts/client";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { DataTable, type DataTableColumn } from "@kopi/ui/components/data-table";
import { toast } from "@kopi/ui/components/sonner";
import { useInfiniteQuery, useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { DownloadIcon, EyeIcon, GitMergeIcon, PencilIcon, PlusIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useCurrentOutlet } from "@/components/current-outlet";
import { Page, PageHeader } from "@/components/page-header";
import { CustomerDialog, type CustomersT } from "@/customers/customer-dialog";
import { customersQuery } from "@/customers/queries";
import { customers } from "@/i18n/messages/customers";
import { useI18n, useMessages } from "@/i18n/provider";
import { canAt, canTenantWide } from "@/lib/access";
import { apiUrl } from "@/lib/api";
import { meScopesQuery } from "@/lib/queries";
import { sortSearch, urlSorting } from "@/lib/sorting";

/** The column sort lives in the URL (R1-09d), so it survives a reload. */
const validateSearch = sortSearch(CUSTOMER_SORTS);

export const Route = createFileRoute("/_authed/customers/")({
  staticData: { section: "workspace", crumb: "nav.customers" },
  validateSearch,
  // No loader: it would pull the customer queries into the shared route tree (cashier bundle).
  component: CustomersPage,
});

function Day(props: { iso: string }) {
  const { format } = useI18n();
  return <time dateTime={props.iso}>{format.date(new Date(props.iso))}</time>;
}

const columnsFor = (t: CustomersT): DataTableColumn<CustomerSummary>[] => [
  {
    id: "name",
    header: t("customers.name"),
    enableSorting: true,
    meta: { card: "title" },
    cell: ({ row }) => (
      <Link
        to="/customers/$customerId"
        params={{ customerId: row.original.id }}
        className="flex min-w-0 flex-col hover:underline"
      >
        <span className="truncate font-medium">{row.original.displayName}</span>
      </Link>
    ),
  },
  {
    id: "contact",
    header: t("customers.contact"),
    cell: ({ row }) => {
      const { phone, email } = row.original;
      return phone || email ? (
        <span className="flex min-w-0 flex-col text-sm">
          {phone && <span className="truncate tabular-nums">{phone}</span>}
          {email && <span className="truncate text-muted-foreground">{email}</span>}
        </span>
      ) : (
        <span className="text-muted-foreground text-sm">{t("customers.noContact")}</span>
      );
    },
  },
  {
    id: "marketing",
    header: t("customers.marketing"),
    meta: { className: "md:w-32" },
    cell: ({ row }) =>
      row.original.marketingConsent ? (
        <Badge variant="success">{t("customers.consents")}</Badge>
      ) : (
        <Badge variant="outline">{t("customers.noConsent")}</Badge>
      ),
  },
  {
    id: "createdAt",
    header: t("customers.since"),
    enableSorting: true,
    meta: { className: "md:w-36" },
    cell: ({ row }) => <Day iso={row.original.createdAt} />,
  },
];

/** Asks the API for the consenting customers' CSV and saves it (owner only; the API enforces it). */
async function downloadMarketingList(): Promise<number> {
  const response = await fetch(apiUrl("/v1/customers/marketing-export"), { credentials: "include" });
  if (!response.ok) throw new Error(`Export failed: ${response.status}`);
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = `pelanggan-promo-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
  return Number(response.headers.get("x-export-count") ?? 0);
}

/**
 * Customers (R1-20, CRM-01) for managers: search the list on the server, add a customer, open one's
 * history, review duplicates and — for the owner — export the promotion list of consenting customers.
 */
function CustomersPage() {
  const { t } = useMessages(customers);
  const columns = useMemo(() => columnsFor(t), [t]);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { outlet } = useCurrentOutlet();
  const navigate = useNavigate({ from: Route.fullPath });
  const { sort } = Route.useSearch();
  const sorting = urlSorting(CUSTOMER_SORTS, DEFAULT_CUSTOMER_SORT, sort, (next) => {
    void navigate({ search: next ? { sort: next } : {}, replace: true });
  });
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState<{ kind: "add" } | { kind: "edit"; customer: CustomerSummary } | null>(
    null,
  );
  const list = useInfiniteQuery(customersQuery(q, sort));
  const mayAdd = Boolean(
    outlet && (canAt(scopes, "customers.manage", outlet.id) || canAt(scopes, "sales.create", outlet.id)),
  );
  const mayExport = canTenantWide(scopes, "customers.manage");

  const exportList = useMutation({
    mutationFn: downloadMarketingList,
    onSuccess: (count) => toast.success(t("customers.exported", { count })),
    onError: () => toast.error(t("customers.exportFailed")),
  });

  return (
    <Page>
      <PageHeader
        title={t("nav.customers")}
        subtitle={t("customers.subtitle")}
        actions={
          <>
            <Button type="button" variant="outline" asChild>
              <Link to="/customers/duplicates">
                <GitMergeIcon />
                {t("customers.duplicates")}
              </Link>
            </Button>
            {mayExport && (
              <Button
                type="button"
                variant="outline"
                disabled={exportList.isPending}
                onClick={() => exportList.mutate()}
              >
                <DownloadIcon />
                {t(exportList.isPending ? "customers.exporting" : "customers.export")}
              </Button>
            )}
            {mayAdd && (
              <Button type="button" onClick={() => setDialog({ kind: "add" })}>
                <PlusIcon />
                {t("customers.add")}
              </Button>
            )}
          </>
        }
      />
      <DataTable
        paging="pages"
        label={t("customers.list")}
        columns={columns}
        query={list}
        getRowId={(customer) => customer.id}
        search={{ value: q, onChange: setQ, placeholder: t("customers.search") }}
        {...sorting}
        rowActions={(customer) => [
          {
            label: t("customers.view"),
            icon: <EyeIcon />,
            onSelect: () =>
              void navigate({ to: "/customers/$customerId", params: { customerId: customer.id } }),
          },
          {
            label: t("customers.edit"),
            icon: <PencilIcon />,
            onSelect: () => setDialog({ kind: "edit", customer }),
          },
        ]}
        empty={{ title: t("customers.emptyTitle"), description: t("customers.emptyHint") }}
      />
      {dialog?.kind === "add" && outlet && (
        <CustomerDialog outletId={outlet.id} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === "edit" && (
        <CustomerDialog customer={dialog.customer} onClose={() => setDialog(null)} />
      )}
    </Page>
  );
}
